import { Link } from "react-router-dom";
import { StatCardSkeleton } from "../loading/SkeletonBlocks.jsx";
import { formatMoneyCents } from "../../lib/money.js";

function EarningsStatCard({ label, value, hint }) {
  return (
    <div className="rounded-[14px] border border-hc-surface-border bg-hc-surface p-4">
      <p className="text-xs text-hc-text-dim">{label}</p>
      <p className="mt-1 text-2xl font-bold tracking-[-0.45px] text-hc-text">{value}</p>
      {hint ? <p className="mt-2 text-xs text-hc-text-dim">{hint}</p> : null}
    </div>
  );
}

function ConnectSetupBanner({ connect, onBeginOnboarding, actionBusy }) {
  const started = Boolean(connect?.accountId);
  return (
    <div className="rounded-[14px] border border-hc-surface-border bg-hc-surface p-4">
      <p className="text-sm text-hc-text-muted">
        Set up payouts to see earnings from client plans and program sales.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={actionBusy}
          onClick={onBeginOnboarding}
          className="rounded-[10px] bg-hc-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
        >
          {actionBusy ? "Redirecting…" : started ? "Continue setup" : "Set up payouts"}
        </button>
        <Link
          to="/billing"
          className="rounded-[10px] border border-hc-surface-border-strong px-4 py-2 text-sm font-medium text-hc-text-muted transition hover:bg-white/5"
        >
          Go to billing
        </Link>
      </div>
    </div>
  );
}

export default function ConnectEarningsSection({
  summary,
  connect,
  connectLoading,
  summaryLoading,
  error,
  onBeginOnboarding,
  actionBusy,
}) {
  const currency = summary?.balance?.currency ?? "cad";
  const stats = summary?.stats ?? {};
  const showSetup = !connectLoading && !connect?.ready;
  const showSkeleton = connectLoading || (connect?.ready && summaryLoading);
  const showContent = connect?.ready && summary?.ready;

  return (
    <section className="mt-8">
      <div className="mb-4">
        <h2 className="text-sm font-semibold tracking-[-0.15px] text-hc-text">Earnings</h2>
        <p className="mt-1 text-xs text-hc-text-dim">
          Client plans and program sales.
        </p>
      </div>

      {error ? (
        <p className="mb-4 rounded-[10px] border border-hc-error/30 bg-hc-error/10 px-3 py-2 text-xs text-hc-error">
          {error}
        </p>
      ) : null}

      {showSetup ? (
        <ConnectSetupBanner
          connect={connect}
          onBeginOnboarding={onBeginOnboarding}
          actionBusy={actionBusy}
        />
      ) : showSkeleton ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>
      ) : showContent ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <EarningsStatCard
            label="Available"
            value={formatMoneyCents(summary.balance?.availableCents, currency)}
            hint="Ready for payout"
          />
          <EarningsStatCard
            label="Pending"
            value={formatMoneyCents(summary.balance?.pendingCents, currency)}
            hint="Processing"
          />
          <EarningsStatCard
            label="Paying clients"
            value={String(stats.activePayingClients ?? 0)}
          />
          <EarningsStatCard
            label="MRR"
            value={`${formatMoneyCents(stats.mrrCents, currency)} / mo`}
          />
        </div>
      ) : (
        <p className="text-xs text-hc-text-dim">
          Earnings data is unavailable right now. Try again from{" "}
          <Link to="/billing" className="text-hc-text-muted underline-offset-2 hover:underline">
            Billing
          </Link>
          .
        </p>
      )}
    </section>
  );
}
